import { CONDITION_CODES } from '@constants/extension-codes';

import { formatFhirDate, getFhirDate } from '@utils/fhir';

import type { ConditionModel, ConditionModelInput } from 'generated/types';
import { ConditionClinicalStatusCode } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type ConditionValues = Pick<
  MedicalConditionRecord,
  | 'category'
  | 'clinicalStatus'
  | 'icd10Code'
  | 'onsetDateTime'
  | 'selfReported'
  | 'snomedCode'
  | 'vitallCode'
> & {
  // condition
  title: string;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & ConditionValues;

export class MedicalConditionRecord extends FhirResource {
  protected model: ConditionModel;

  public static readonly ICD_10_SYSTEM = 'http://hl7.org/fhir/sid/icd-10';

  public static readonly SNOMED_SYSTEM = 'http://snomed.info/sct';

  private static readonly ICD_10_CM_SYSTEM =
    'http://hl7.org/fhir/sid/icd-10-cm';

  public constructor(model: ConditionModel) {
    super(model);
    this.model = model;
  }

  public get recordType() {
    return this.model.__typename;
  }

  public get condition() {
    const condition = this.model.code.text;
    return condition;
  }

  public set condition(value: string | null | undefined) {
    this.model = {
      ...this.model,
      code: { text: value },
    };
  }

  public get category() {
    return this.model.category?.map(({ text }) => text).join('/');
  }

  public set category(value: string | null | undefined) {
    this.model = {
      ...this.model,
      category: [
        {
          text: value,
        },
      ],
    };
  }

  public get snomedCode() {
    const code =
      this.findExtension(CONDITION_CODES)?.valueCodeableConcept ??
      this.model.code;
    return code.coding?.find(
      ({ system }) => system === MedicalConditionRecord.SNOMED_SYSTEM
    )?.display;
  }

  public set snomedCode(value: string | null | undefined) {
    this.addOrReplaceExtensionCoding(
      CONDITION_CODES,
      MedicalConditionRecord.SNOMED_SYSTEM,
      value
    );
  }

  public get icd10Code() {
    const code =
      this.findExtension(CONDITION_CODES)?.valueCodeableConcept ??
      this.model.code;
    return code.coding?.find(
      ({ system }) =>
        system === MedicalConditionRecord.ICD_10_SYSTEM ||
        system === MedicalConditionRecord.ICD_10_CM_SYSTEM
    )?.display;
  }

  public set icd10Code(value: string | null | undefined) {
    this.addOrReplaceExtensionCoding(
      CONDITION_CODES,
      MedicalConditionRecord.ICD_10_SYSTEM,
      value
    );
  }

  public get vitallCode() {
    const extension = this.findExtension(CONDITION_CODES);
    return extension?.valueCodeableConcept?.coding?.find(
      ({ system }) => system === MedicalConditionRecord.VITALL_SYSTEM
    )?.display;
  }

  public set vitallCode(value: string | null | undefined) {
    this.addOrReplaceExtensionCoding(
      CONDITION_CODES,
      MedicalConditionRecord.VITALL_SYSTEM,
      value
    );
  }

  public get clinicalStatus() {
    return this.model.clinicalStatus?.text;
  }

  public set clinicalStatus(value: string | null | undefined) {
    this.model = {
      ...this.model,
      clinicalStatus: value
        ? {
            coding: [
              {
                code:
                  value === 'inactive'
                    ? ConditionClinicalStatusCode.inactive
                    : ConditionClinicalStatusCode.active,
                display: value === 'inactive' ? 'Inactive' : 'Active',
                system:
                  'http://terminology.hl7.org/CodeSystem/condition-clinical',
              },
            ],
            text: value === 'inactive' ? 'Inactive' : 'Active',
          }
        : null,
    };
  }

  public get onsetDateTime() {
    return this.model.onsetDateTime;
  }

  public set onsetDateTime(value: string | null | undefined) {
    this.model = {
      ...this.model,
      onsetDateTime: value,
    };
  }

  public getOnsetDate() {
    return getFhirDate(this.model.onsetDateTime);
  }

  public getConditionOnset() {
    const { model } = this;
    const date = model.onsetDateTime;
    const range = model.onsetRange;
    const period = model.onsetPeriod;
    const age = model.onsetAge;
    if (date) {
      return formatFhirDate(date, { showTime: false });
    } else if (range) {
      const start = range.low?.value;
      const startUnit = range.low?.unit;
      const end = range.high?.value;
      const endUnit = range.high?.unit;
      return `${start}${startUnit || ''} - ${end}${endUnit || ''}`;
    } else if (period) {
      return `${formatFhirDate(period.start)}${
        period.end ? `- ${formatFhirDate(period.end)}` : ''
      }`;
    } else if (age) {
      return `${age.value}${age.unit ? ` ${age.unit}` : ''}`;
    }
    return null;
  }

  public isActive() {
    return this.model.clinicalStatus?.coding?.some(
      ({ code }) => code === 'active'
    );
  }

  public getModelInput(values?: ConditionValues): ConditionModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.condition = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return {
      ...cleaned,
    };
  }
}
